/**
 * Email Queue Modal
 * View and edit email queue items
 */

import { useEffect } from 'react';
import { useForm } from '@mantine/form';
import {
  IconMail,
  IconClock,
  IconAlertCircle,
  IconCheck,
} from '@tabler/icons-react';
import { Stack, Group, Button, Alert, Text, Select, TextInput, Badge, Code } from '../../../../../components/ui';
import { Modal, Paper } from '../../../../../components/ui';
import { HtmlEditor } from '../../../../../components/ui';
import { StatusTracker } from '../../../../../components/advanced/workflow';
import type { EmailQueueModalProps } from '../types';
import { getStatusColor, formatDateTime, PRIORITIES } from '../../types';

export function EmailQueueModal({
  opened,
  onClose,
  item,
  mode,
  onSave,
  loading,
}: EmailQueueModalProps) {
  const isEditMode = mode === 'edit';
  const isViewMode = mode === 'view';

  const form = useForm({
    initialValues: {
      toEmail: '',
      cc: '',
      bcc: '',
      subject: '',
      body: '',
      priority: 5,
    },
  });

  useEffect(() => {
    if (item) {
      form.setValues({
        toEmail: item.recipient || '',
        cc: item.cc || '',
        bcc: item.bcc || '',
        subject: item.subject || '',
        body: item.body || '',
        priority: item.priority ?? 5,
      });
    }
  }, [item]);

  const handleSubmit = async (values: typeof form.values) => {
    if (onSave && item) {
      await onSave({
        ...item,
        ...values,
      } as any);
      onClose();
    }
  };

  const handleClose = () => {
    form.reset();
    onClose();
  };

  if (!item) return null;

  // Define status tracker steps
  const statusSteps = [
    {
      key: 'queued',
      label: 'Queued',
      description: 'Added to queue',
      timestamp: item.createdDateTime ? new Date(item.createdDateTime) : undefined,
    },
    {
      key: 'delivered',
      label: 'Delivered',
      description: 'Delivered to recipient',
      timestamp: item.deliveredDateTime ? new Date(item.deliveredDateTime) : undefined,
    },
    {
      key: 'opened',
      label: 'Opened',
      description: 'Email opened',
      timestamp: item.openedDateTime ? new Date(item.openedDateTime) : undefined,
    },
    {
      key: 'clicked',
      label: 'Clicked',
      description: 'Link clicked',
      timestamp: item.clickedDateTime ? new Date(item.clickedDateTime) : undefined,
    },
    {
      key: 'read',
      label: 'Read',
      description: 'Email read',
      timestamp: item.readDateTime ? new Date(item.readDateTime) : undefined,
    },
  ];

  const currentStep = item.status?.toLowerCase() || 'queued';
  const completedSteps = [];
  if (item.createdDateTime) completedSteps.push('queued');
  if (item.deliveredDateTime) completedSteps.push('delivered');
  if (item.openedDateTime) completedSteps.push('opened');
  if (item.clickedDateTime) completedSteps.push('clicked');
  if (item.readDateTime) completedSteps.push('read');

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={isEditMode ? 'Edit Email' : 'Email Details'}
      
      size="xl"
    >
      <Stack gap="md">
        {/* Status Badge */}
        <Group>
          <Badge
            variant="light"
            gradientVariant={getStatusColor(item.status as any)}
            size="lg"
          >
            {item.status}
          </Badge>
        </Group>

        {/* Status Tracker */}
        <Paper p="md" withBorder>
          <StatusTracker
            title="Delivery Status"
            variant="timeline"
            steps={statusSteps}
            currentStatus={currentStep}
            completedSteps={completedSteps}
            failedStep={item.status === 'Failed' ? currentStep : undefined}
            showTimestamps
            orientation="vertical"
          />
        </Paper>

        {/* Error Alert - Email doesn't have errorMessage field in SDK */}
        {item.status === 'Failed' && (
          <Alert
            color="red"
            icon={<IconAlertCircle size={16} />}
            title="Delivery Failed"
          >
            This email failed to send. Please check the email configuration and try again.
          </Alert>
        )}

        {/* Form/View Fields */}
        {isEditMode ? (
          <form onSubmit={form.onSubmit(handleSubmit)}>
            <Stack gap="md">
              <TextInput
                label="Recipient"
                required
                {...form.getInputProps('toEmail')}
              />

              <Group grow>
                <TextInput
                  label="CC"
                  {...form.getInputProps('cc')}
                />
                <TextInput
                  label="BCC"
                  {...form.getInputProps('bcc')}
                />
              </Group>

              <TextInput
                label="Subject"
                required
                {...form.getInputProps('subject')}
              />

              <HtmlEditor
                label="Body"
                value={form.values.body}
                onChange={(val) => form.setFieldValue('body', val)}
              />

              <Select
                label="Priority"
                data={PRIORITIES.map((p) => ({
                  value: String(p.value),
                  label: p.label,
                }))}
                value={String(form.values.priority)}
                onChange={(val) => form.setFieldValue('priority', parseInt(val || '5', 10))}
              />

              <Group justify="flex-end" gap="xs">
                <Button variant="subtle" onClick={handleClose}>
                  Cancel
                </Button>
                <Button type="submit" loading={loading} variant="light">
                  Save Changes
                </Button>
              </Group>
            </Stack>
          </form>
        ) : (
          <Stack gap="md">
            <div>
              <Text fw={500} size="sm" mb={4}>
                Recipient
              </Text>
              <Text size="sm">{item.recipient}</Text>
            </div>

            {item.cc && (
              <div>
                <Text fw={500} size="sm" mb={4}>
                  CC
                </Text>
                <Text size="sm">{item.cc}</Text>
              </div>
            )}

            {item.bcc && (
              <div>
                <Text fw={500} size="sm" mb={4}>
                  BCC
                </Text>
                <Text size="sm">{item.bcc}</Text>
              </div>
            )}

            <div>
              <Text fw={500} size="sm" mb={4}>
                Subject
              </Text>
              <Text size="sm">{item.subject}</Text>
            </div>

            <div>
              <Text fw={500} size="sm" mb={4}>
                Priority
              </Text>
              <Badge>{item.priority ?? 5}</Badge>
            </div>

            <div>
              <Text fw={500} size="sm" mb={4}>
                Body
              </Text>
              <Paper p="md" withBorder>
                {item.isHtml ? (
                  <div dangerouslySetInnerHTML={{ __html: (() => { try { return item.body ? atob(item.body) : ''; } catch (e) { return item.body || ''; } })() }} />
                ) : (
                  <Text style={{ whiteSpace: 'pre-wrap' }}>{(() => { try { return item.body ? atob(item.body) : ''; } catch (e) { return item.body || ''; } })()}</Text>
                )}
              </Paper>
            </div>

            <Group justify="flex-end">
              <Button variant="subtle" onClick={handleClose}>
                Close
              </Button>
            </Group>
          </Stack>
        )}
      </Stack>
    </Modal>
  );
}
